import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowLeft, CheckCircle } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { getAgentUrl } from '../../services/agent-url'

export default function BugReportPage() {
  const { session, user } = useAuth()
  const location = useLocation()
  const [name, setName] = useState('')
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [page, setPage] = useState(location.pathname)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!subject.trim() || !description.trim()) return
    setSubmitting(true)
    setError(null)

    try {
      const baseUrl = await getAgentUrl()
      const res = await fetch(`${baseUrl}/api/bug-reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          title: subject.trim(),
          description: `${name.trim() ? `Name: ${name.trim()}\n\n` : ''}${description.trim()}`,
          pageUrl: page.trim() || undefined,
        }),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to submit')
      }

      setSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="text-center space-y-4">
          <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto" />
          <h2 className="font-mono text-lg text-slate-100">Bug Report Submitted</h2>
          <p className="font-mono text-sm text-slate-500">Thanks for helping us improve GCodr.</p>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 font-mono text-xs text-cyan-400 hover:text-cyan-300 transition-colors mt-4"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  const inputClass = "w-full bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-2 font-mono text-sm text-slate-200 placeholder:text-slate-600 focus:border-cyan-400 focus:outline-none transition-colors"

  return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center px-4">
      <div className="w-full max-w-lg">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 font-mono text-xs text-cyan-400/70 hover:text-cyan-400 transition-colors mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Dashboard
        </Link>

        <h1 className="font-mono text-xl text-slate-100 mb-1">Report a Bug</h1>
        <p className="font-mono text-sm text-slate-500 mb-6">Help us fix issues by describing what went wrong.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-mono text-[10px] text-slate-500 tracking-widest uppercase mb-1.5">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={user?.email || 'Your name'}
              className={inputClass}
            />
          </div>

          <div>
            <label className="block font-mono text-[10px] text-slate-500 tracking-widest uppercase mb-1.5">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Brief summary of the issue"
              required
              className={inputClass}
            />
          </div>

          <div>
            <label className="block font-mono text-[10px] text-slate-500 tracking-widest uppercase mb-1.5">
              Description
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What happened? What did you expect? Steps to reproduce..."
              required
              rows={5}
              className={`${inputClass} resize-none`}
            />
          </div>

          <div>
            <label className="block font-mono text-[10px] text-slate-500 tracking-widest uppercase mb-1.5">
              Page
            </label>
            <input
              type="text"
              value={page}
              onChange={e => setPage(e.target.value)}
              placeholder="Which page were you on?"
              className={inputClass}
            />
          </div>

          {error && (
            <p className="font-mono text-xs text-red-400">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting || !subject.trim() || !description.trim()}
            className="w-full py-2.5 rounded-lg font-mono text-sm bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            {submitting ? 'Submitting...' : 'Submit Bug Report'}
          </button>
        </form>
      </div>
    </div>
  )
}
